import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { formatarMoeda, formatarData } from '../contasPagar/utils/formatadores';

const TabelaRelatorios = ({ dados, onDiaClick }) => {
  const [linhasExpandidas, setLinhasExpandidas] = useState(new Set());

  const toggleLinha = (index) => {
    const newExpanded = new Set(linhasExpandidas);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setLinhasExpandidas(newExpanded);
  };

  const handleExpandKeyDown = (event, index, canExpand) => {
    if (!canExpand) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleLinha(index);
    }
  };

  // Layout Mobile: Cards verticais
  const renderMobileView = () => {
    return (
      <div className="space-y-3 md:hidden">
        {dados.map((item, index) => {
          const isExpanded = linhasExpandidas.has(index);
          const historico = item.historico || [];
          const temHistorico = historico.length > 0;

          return (
            <div key={index} className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg">
              {/* Card principal */}
              <div 
                className={`p-4 ${temHistorico ? 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40' : ''}`}
                onClick={() => temHistorico && toggleLinha(index)}
                onKeyDown={(event) => handleExpandKeyDown(event, index, temHistorico)}
                role={temHistorico ? 'button' : undefined}
                tabIndex={temHistorico ? 0 : undefined}
                aria-expanded={temHistorico ? isExpanded : undefined}
              >
                {/* Header do card com nome da conta */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[color:var(--text-primary)] mb-1">
                      {item.nome}
                    </div>
                    {item.fornecedoresUnicos > 0 && (
                      <div className="text-xs text-[color:var(--text-muted)]">
                        {item.fornecedoresUnicos} fornecedor{item.fornecedoresUnicos !== 1 ? 'es' : ''}
                      </div>
                    )}
                  </div>
                  {temHistorico && (
                    <div className="ml-2 flex-shrink-0">
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5 text-[color:var(--text-muted)]" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-[color:var(--text-muted)]" />
                      )}
                    </div>
                  )}
                </div>

                {/* Valores em grid compacto */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col min-w-0">
                    <div className="text-[10px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide mb-1.5">
                      Saldo Inicial
                    </div>
                    <div className="text-sm font-semibold text-[color:var(--text-primary)] leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                      {formatarMoeda(item.saldo_inicial || item.saldoInicial || 0)}
                    </div>
                  </div>
                  
                  <div className="flex flex-col min-w-0">
                    <div className="text-[10px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide mb-1.5">
                      Saldo
                    </div>
                    <div className="text-sm font-semibold text-[color:var(--text-primary)] leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                      {formatarMoeda(item.saldo || item.valorTotal || 0)}
                    </div>
                  </div>
                  
                  <div className="flex flex-col min-w-0">
                    <div className="text-[10px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide mb-1.5">
                      Entrada
                    </div>
                    <div className="text-sm font-semibold text-[color:var(--chart-positive)] leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                      {formatarMoeda(item.entrada || item.valorPago || 0)}
                    </div>
                  </div>
                  
                  <div className="flex flex-col min-w-0">
                    <div className="text-[10px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide mb-1.5">
                      Saída
                    </div>
                    <div className="text-sm font-semibold text-[color:var(--chart-negative)] leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                      {formatarMoeda(item.saida || item.valorAberto || 0)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção expandida com histórico */}
              {isExpanded && temHistorico && (
                <div className="border-t border-[color:var(--border)] bg-[color:var(--surface-muted)] p-4">
                  <div className="text-xs font-semibold text-[color:var(--text-primary)] mb-3">
                    Transações Diárias ({historico.length} registro{historico.length !== 1 ? 's' : ''})
                  </div>
                  <div className="space-y-2">
                    {historico.map((transacao, transIndex) => (
                      <div
                        key={transIndex}
                        className={`bg-[color:var(--surface)] rounded-lg p-3 border border-[color:var(--border)] ${onDiaClick ? 'cursor-pointer hover:border-[color:var(--accent)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onDiaClick) {
                            onDiaClick({
                              conta: item,
                              data: transacao.data,
                              transacao: transacao
                            });
                          }
                        }}
                        onKeyDown={(e) => {
                          if (!onDiaClick) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            onDiaClick({
                              conta: item,
                              data: transacao.data,
                              transacao: transacao
                            });
                          }
                        }}
                        role={onDiaClick ? 'button' : undefined}
                        tabIndex={onDiaClick ? 0 : undefined}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-xs font-medium text-[color:var(--text-primary)]">
                            {formatarData(transacao.data) || '-'}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="flex flex-col min-w-0">
                            <div className="text-[color:var(--text-muted)] mb-1.5 text-[10px] font-medium uppercase tracking-wide">Entrada</div>
                            <div className="font-semibold text-[color:var(--chart-positive)] leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                              {formatarMoeda(parseFloat(transacao.entrada || transacao.recebido || 0))}
                            </div>
                          </div>
                          <div className="flex flex-col min-w-0">
                            <div className="text-[color:var(--text-muted)] mb-1.5 text-[10px] font-medium uppercase tracking-wide">Saída</div>
                            <div className="font-semibold text-[color:var(--chart-negative)] leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                              {formatarMoeda(parseFloat(transacao.saida || 0))}
                            </div>
                          </div>
                          <div className="flex flex-col min-w-0">
                            <div className="text-[color:var(--text-muted)] mb-1.5 text-[10px] font-medium uppercase tracking-wide">Saldo</div>
                            <div className="font-semibold text-[color:var(--text-primary)] leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                              {formatarMoeda(parseFloat(transacao.saldo_diario || 0))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Layout Desktop: Tabela tradicional
  const renderDesktopView = () => {
    return (
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-[color:var(--border)]">
          <thead className="bg-[color:var(--surface-muted)]">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider w-12">
                {/* Coluna para ícone de expansão */}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                Conta Bancária
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                Saldo Inicial
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                Entrada
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                Saída
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                Saldo
              </th>
            </tr>
          </thead>
          <tbody className="bg-[color:var(--surface)] divide-y divide-[color:var(--border)]">
            {dados.map((item, index) => {
              const isExpanded = linhasExpandidas.has(index);
              const historico = item.historico || [];
              const temHistorico = historico.length > 0;

              return (
                <React.Fragment key={index}>
                  {/* Linha principal da conta */}
                  <tr 
                    className={`hover:bg-[color:var(--surface-muted)] ${temHistorico ? 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40' : ''}`}
                    onClick={() => temHistorico && toggleLinha(index)}
                    onKeyDown={(event) => handleExpandKeyDown(event, index, temHistorico)}
                    role={temHistorico ? 'button' : undefined}
                    tabIndex={temHistorico ? 0 : undefined}
                    aria-expanded={temHistorico ? isExpanded : undefined}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      {temHistorico ? (
                        isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-[color:var(--text-muted)]" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-[color:var(--text-muted)]" />
                        )
                      ) : (
                        <div className="w-4 h-4" />
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-[color:var(--text-primary)]">
                      <div>
                        <div className="font-medium">{item.nome}</div>
                        {item.fornecedoresUnicos > 0 && (
                          <div className="text-xs text-[color:var(--text-muted)]">
                            {item.fornecedoresUnicos} fornecedor{item.fornecedoresUnicos !== 1 ? 'es' : ''}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-[color:var(--text-primary)]">
                      {formatarMoeda(item.saldo_inicial || item.saldoInicial || 0)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-[color:var(--chart-positive)]">
                      {formatarMoeda(item.entrada || item.valorPago || 0)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-[color:var(--chart-negative)]">
                      {formatarMoeda(item.saida || item.valorAberto || 0)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-[color:var(--text-primary)]">
                      {formatarMoeda(item.saldo || item.valorTotal || 0)}
                    </td>
                  </tr>

                  {/* Linha expandida com transações diárias */}
                  {isExpanded && temHistorico && (
                    <tr>
                      <td colSpan={6} className="px-0 py-0 bg-[color:var(--surface-muted)]">
                        <div className="px-4 py-4">
                          <div className="text-xs font-medium text-[color:var(--text-primary)] mb-3">
                            Transações Diárias ({historico.length} registro{historico.length !== 1 ? 's' : ''})
                          </div>
                          <div className="border border-[color:var(--border)] rounded-lg overflow-hidden bg-[color:var(--surface)]">
                            <table className="min-w-full divide-y divide-[color:var(--border)]">
                              <thead className="bg-[color:var(--surface-muted)]">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase">
                                    Data
                                  </th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-[color:var(--text-muted)] uppercase">
                                    Entrada
                                  </th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-[color:var(--text-muted)] uppercase">
                                    Saída
                                  </th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-[color:var(--text-muted)] uppercase">
                                    Saldo Diário
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-[color:var(--surface)] divide-y divide-[color:var(--border)]">
                                {historico.map((transacao, transIndex) => (
                                  <tr 
                                    key={transIndex}
                                    className={`hover:bg-[color:var(--surface-muted)] transition-colors ${onDiaClick ? 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (onDiaClick) {
                                        onDiaClick({
                                          conta: item,
                                          data: transacao.data,
                                          transacao: transacao
                                        });
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (!onDiaClick) return;
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onDiaClick({
                                          conta: item,
                                          data: transacao.data,
                                          transacao: transacao
                                        });
                                      }
                                    }}
                                    role={onDiaClick ? 'button' : undefined}
                                    tabIndex={onDiaClick ? 0 : undefined}
                                  >
                                    <td className="px-3 py-2 text-sm text-[color:var(--text-primary)]">
                                      {formatarData(transacao.data) || '-'}
                                    </td>
                                    <td className="px-3 py-2 text-sm text-[color:var(--chart-positive)] text-right">
                                      {formatarMoeda(parseFloat(transacao.entrada || transacao.recebido || 0))}
                                    </td>
                                    <td className="px-3 py-2 text-sm text-[color:var(--chart-negative)] text-right">
                                      {formatarMoeda(parseFloat(transacao.saida || 0))}
                                    </td>
                                    <td className="px-3 py-2 text-sm text-[color:var(--text-primary)] text-right font-medium">
                                      {formatarMoeda(parseFloat(transacao.saldo_diario || 0))}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      {renderMobileView()}
      {renderDesktopView()}
    </>
  );
};

export default TabelaRelatorios;
